import { Fragment, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { trpc } from "../../lib/trpc";
import { PoAttachmentPanel } from "./PoAttachmentPanel";
import { QuotesPanel } from "./QuotesPanel";
import { PriceHistoryTable, fmtMoney } from "./PriceHistoryTable";
import { PaymentTermsFields, paymentTermsToInput, PAYMENT_METHOD_LABEL, type PaymentTerms } from "./PaymentTermsFields";
import { parseTrNumber } from "../../lib/trNumber";
import "./Purchasing.css";

interface PoDetailProps {
  open: boolean;
  poId: string | null;
  onClose: () => void;
}

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  draft: { cls: "", label: "Taslak" },
  confirmed: { cls: "b-warn", label: "Onaylı" },
  partially_received: { cls: "b-warn", label: "Kısmi Teslim" },
  received: { cls: "b-success", label: "Teslim Alındı" },
  cancelled: { cls: "", label: "İptal" },
};

function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n); }
function fmtDate(d: Date | string | null): string { return d ? new Date(d).toLocaleDateString("tr-TR") : "—"; }
/** Date → yerel "YYYY-MM-DD" (<input type=date> için). */
function ymdLocal(d: Date | string | null): string {
  if (!d) return "";
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

export function PoDetailDrawer({ open, poId, onClose }: PoDetailProps) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  // Teslim alma
  const [receivingLine, setReceivingLine] = useState<string | null>(null);
  const [recvQty, setRecvQty] = useState("");
  const [recvLoc, setRecvLoc] = useState("");
  const [recvLot, setRecvLot] = useState("");
  const [recvPrice, setRecvPrice] = useState("");
  const [recvErr, setRecvErr] = useState<string | null>(null);
  // Fiyat geçmişi açılır satırlar
  const [histOpen, setHistOpen] = useState<Set<string>>(new Set());
  // Düzenleme + onaylama
  const [editing, setEditing] = useState(false);
  const [editReq, setEditReq] = useState("");
  const [editSup, setEditSup] = useState("");
  const [editExpected, setEditExpected] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editPt, setEditPt] = useState<PaymentTerms | null>(null);
  const [editLinePrices, setEditLinePrices] = useState<Record<string, string>>({});
  const [editErr, setEditErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [approverId, setApproverId] = useState("");
  const [reopenOpen, setReopenOpen] = useState(false);

  const poQ = trpc.purchasing.get.useQuery({ id: poId! }, { enabled: open && !!poId });
  const locsQ = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const usersQ = trpc.purchasing.assignableUsers.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100, isActive: true }, { enabled: open, staleTime: 60_000 });
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const confirmMut = trpc.purchasing.confirm.useMutation();
  const receiveMut = trpc.purchasing.receiveLine.useMutation();
  const updateMut = trpc.purchasing.update.useMutation();
  const reopenMut = trpc.purchasing.reopen.useMutation();

  const p = poQ.data;

  useEffect(() => {
    setReceivingLine(null); setRecvErr(null); setRecvQty(""); setRecvLoc(""); setRecvLot(""); setRecvPrice("");
    setHistOpen(new Set()); setEditing(false); setConfirming(false); setEditErr(null);
  }, [poId, open]);

  const userOptions = useMemo<SSOption[]>(() => (usersQ.data ?? []).map((u) => ({ value: u.id, label: u.name })), [usersQ.data]);
  const supplierOptions = useMemo<SSOption[]>(() => (suppliersQ.data?.rows ?? []).map((s) => ({ value: s.id, label: s.name, tail: s.code })), [suppliersQ.data]);
  const sb = p ? STATUS_BADGE[p.status] : null;
  const isDraft = p?.status === "draft";
  const cur = p?.currency ?? "TRY";

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try { await fn(); await qc.invalidateQueries(); } finally { setBusy(false); }
  }

  function startReceive(lineId: string, remaining: number) {
    setReceivingLine(lineId); setRecvQty(String(remaining)); setRecvLoc(""); setRecvLot(""); setRecvPrice(""); setRecvErr(null);
  }
  async function doReceive(lineId: string) {
    setRecvErr(null);
    if (!recvQty || Number(recvQty) <= 0) { setRecvErr("Geçerli bir miktar girin."); return; }
    if (!recvLoc) { setRecvErr("Lokasyon seçin."); return; }
    await run(() => receiveMut.mutateAsync({ poLineId: lineId, qty: Number(recvQty), locationId: recvLoc, supplierLot: recvLot || undefined, unitPrice: recvPrice ? Number(recvPrice) : undefined }));
    setReceivingLine(null);
  }
  // Hizmet satırı: stoğa girmez — kalan miktarı "ifa edildi" işaretle (lokasyon yok).
  async function fulfillService(lineId: string, remaining: number) {
    await run(() => receiveMut.mutateAsync({ poLineId: lineId, qty: remaining }));
  }

  function startEdit() {
    if (!p) return;
    setEditReq(p.requestedById ?? "");
    setEditSup(p.supplierId ?? "");
    setEditExpected(ymdLocal(p.expectedDate));
    setEditNotes(p.notes ?? "");
    setEditPt({
      currency: p.currency,
      paymentMethod: p.paymentMethod ?? "",
      termDays: p.termDays != null ? String(p.termDays) : "",
      installmentCount: p.installmentCount != null ? String(p.installmentCount) : "",
      bankIds: p.banks.map((b) => b.id),
      paymentNotes: p.paymentNotes ?? "",
    });
    setEditLinePrices(Object.fromEntries(p.lines.map((l) => [l.id, l.unitPrice != null ? String(l.unitPrice) : ""])));
    setEditErr(null);
    setEditing(true);
  }
  async function saveEdit() {
    if (!p || !editPt) return;
    setEditErr(null);
    const pin = paymentTermsToInput(editPt);
    // TR biçim ayrıştırma (parseTrNumber): "12.500"→12500, "1.234,56"→1234.56. Naif replace 1000× hata/sessiz kayıp yapardı.
    const lines: Array<{ id: string; unitPrice: number | null }> = [];
    for (const l of p.lines) {
      const raw = editLinePrices[l.id]?.trim();
      if (!raw) { lines.push({ id: l.id, unitPrice: null }); continue; }
      const up = parseTrNumber(raw);
      if (!Number.isFinite(up) || up < 0) { setEditErr(`Geçersiz birim fiyat: "${raw}" (${l.itemCode}). TR biçimi: 12,50 veya 1.234,56.`); return; }
      lines.push({ id: l.id, unitPrice: up });
    }
    try {
      await updateMut.mutateAsync({ id: p.id, requestedById: editReq || null, supplierId: editSup || null, expectedDate: editExpected ? new Date(`${editExpected}T12:00:00`).toISOString() : null, notes: editNotes.trim() || null, lines, ...pin });
      await qc.invalidateQueries();
      setEditing(false);
    } catch (e) { setEditErr((e as Error).message); }
  }

  function startConfirm() {
    setApproverId(meQ.data?.user?.id ?? "");
    setConfirming(true);
  }
  async function doConfirm() {
    if (!p) return;
    await run(() => confirmMut.mutateAsync({ id: p.id, approvedById: approverId || undefined }));
    setConfirming(false);
  }
  async function doReopen() {
    if (!p) return;
    setReopenOpen(false);
    await run(() => reopenMut.mutateAsync({ id: p.id }));
  }

  function toggleHist(itemId: string) {
    setHistOpen((s) => { const n = new Set(s); n.has(itemId) ? n.delete(itemId) : n.add(itemId); return n; });
  }

  const locations = locsQ.data ?? [];

  return (
    <Drawer open={open} onClose={onClose} size="3xl" title={p ? `Satınalma ${p.code}` : "Satınalma Siparişi"} subtitle={p ? (p.supplierName ?? "Tedarikçi atanmadı") : undefined}>
      {!p ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>Yükleniyor…</div>
      ) : (
        <>
          {/* Durum + döviz + yazdır */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span className={`badge-pill ${sb?.cls ?? ""}`}>{sb?.label}</span>
            {p.supplierCode ? <span className="mono" style={{ fontSize: 12, color: "var(--neutral-500)" }}>{p.supplierCode}</span> : <span className="badge-pill b-warn">Tedarikçi atanmadı</span>}
            <span className="badge-pill b-info">{cur}</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn-md secondary" onClick={() => window.open(`/print/satinalma-talebi?id=${p.id}`, "_blank", "noopener")}>
              <Icon name="printer" size={14} />A4 Yazdır
            </button>
            {p.status !== "draft" && p.status !== "cancelled" ? (
              <button type="button" className="btn-md secondary" onClick={() => window.open(`/print/siparis-formu?id=${p.id}`, "_blank", "noopener")} title="Onaylı siparişin resmi formu (tedarikçiye gönderilir)">
                <Icon name="fileText" size={14} />Sipariş Formu
              </button>
            ) : null}
            {isDraft && !editing ? (
              <>
                <button type="button" className="btn-md secondary" onClick={startEdit}><Icon name="edit" size={14} />Düzenle</button>
                <button type="button" className="btn-md primary" onClick={startConfirm} disabled={confirming}><Icon name="check" size={14} />Onayla</button>
              </>
            ) : null}
            {p.status === "confirmed" ? (
              <button type="button" className="btn-md warn" onClick={() => setReopenOpen(true)} disabled={reopenMut.isPending}>
                <Icon name="refresh" size={14} />Onaya Geri Al
              </button>
            ) : null}
          </div>

          {(confirmMut.error || receiveMut.error || reopenMut.error) ? <div className="form-banner-err" role="alert">{(confirmMut.error || receiveMut.error || reopenMut.error)?.message}</div> : null}

          {p.maintenanceOrder ? (
            <div className="form-banner-info" style={{ marginBottom: 10 }}>
              <Icon name="wrench" size={13} /> Bağlı bakım/onarım emri: <b className="mono">{p.maintenanceOrder.code}</b> — {p.maintenanceOrder.title}
            </div>
          ) : null}

          {/* Talep/onay + tarih bilgi ızgarası */}
          <div className="po-kv-grid">
            <div className="po-kv"><span className="k">Talep Eden</span><span className={`v ${p.requestedBy ? "" : "muted"}`}>{p.requestedBy ?? "—"}</span></div>
            <div className="po-kv"><span className="k">Onaylayan</span><span className={`v ${p.approvedBy ? "" : "muted"}`}>{p.approvedBy ?? "—"}{p.approvedAt ? ` · ${fmtDate(p.approvedAt)}` : ""}</span></div>
            <div className="po-kv"><span className="k">Sipariş Tarihi</span><span className="v mono">{fmtDate(p.orderDate)}</span></div>
            <div className="po-kv"><span className="k">Beklenen Tarih</span><span className="v mono">{fmtDate(p.expectedDate)}</span></div>
          </div>

          {/* Ödeme vadesi özeti */}
          <div className="po-sec-title">Ödeme Vadesi</div>
          <div className="po-pay-chips">
            {p.paymentMethod ? <span className="badge-pill b-info">{PAYMENT_METHOD_LABEL[p.paymentMethod] ?? p.paymentMethod}</span> : <span className="badge-pill">Belirtilmedi</span>}
            {p.paymentMethod === "credit_card" && p.installmentCount ? <span className="badge-pill">{p.installmentCount} taksit</span> : null}
            {p.paymentMethod === "credit_card" && p.banks.length ? p.banks.map((b) => <span key={b.id} className="badge-pill">{b.name}</span>) : null}
            {p.paymentMethod === "term" && p.termDays != null ? <span className="badge-pill">{p.termDays} gün vade</span> : null}
            {p.paymentNotes ? <span style={{ fontSize: 12, color: "var(--neutral-500)" }}>· {p.paymentNotes}</span> : null}
          </div>

          {/* Düzenleme paneli (taslak) */}
          {editing && editPt ? (
            <div style={{ marginTop: 14, border: "1px solid var(--primary)", borderRadius: "var(--radius-md)", padding: 14, background: "var(--primary-subtle)" }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 10, color: "var(--primary)" }}>Talebi Düzenle</div>
              {editErr ? <div className="form-banner-err" style={{ marginBottom: 10 }}>{editErr}</div> : null}
              <div className="po-fgrid">
                <div className="form-field">
                  <label className="lbl">Talep Eden</label>
                  <SearchSelect value={editReq || null} options={userOptions} onChange={(v) => setEditReq(v ?? "")} placeholder="— Talep eden —" searchPlaceholder="Kullanıcı ara…" />
                </div>
                <div className="form-field">
                  <label className="lbl">Tedarikçi</label>
                  <SearchSelect value={editSup || null} options={supplierOptions} onChange={(v) => setEditSup(v ?? "")} placeholder="— Tedarikçi (opsiyonel) —" searchPlaceholder="Tedarikçi ara…" clearable />
                </div>
                <div className="form-field">
                  <label className="lbl">Beklenen Teslim Tarihi</label>
                  <input className="form-input" type="date" value={editExpected} onChange={(e) => setEditExpected(e.target.value)} />
                </div>
              </div>
              <PaymentTermsFields value={editPt} onChange={setEditPt} enabled={open} />
              <div className="po-sec-title">Satır Birim Fiyatları ({cur})</div>
              <table className="dt2" style={{ fontSize: 12.5 }}>
                <thead><tr><th>Malzeme</th><th className="num" style={{ width: 110 }}>Miktar</th><th className="num" style={{ width: 150 }}>Birim Fiyat</th></tr></thead>
                <tbody>
                  {p.lines.map((l) => (
                    <tr key={l.id}>
                      <td><span className="mono" style={{ fontSize: 11, color: "var(--neutral-500)", marginRight: 6 }}>{l.itemCode}</span>{l.itemName}</td>
                      <td className="cell-num">{fmtQty(l.qty)} {l.uom}</td>
                      <td className="cell-num">
                        <input className="form-input" style={{ maxWidth: 130, textAlign: "right" }} inputMode="decimal" value={editLinePrices[l.id] ?? ""}
                          onChange={(e) => setEditLinePrices((s) => ({ ...s, [l.id]: e.target.value.replace(/[^\d.,]/g, "") }))} placeholder="0,00" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="form-field" style={{ marginTop: 10 }}>
                <label className="lbl">Sipariş Notu</label>
                <input className="form-input" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} maxLength={2000} placeholder="opsiyonel" />
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                <button type="button" className="btn-md ghost" onClick={() => setEditing(false)} disabled={updateMut.isPending}>Vazgeç</button>
                <button type="button" className="btn-md primary" onClick={saveEdit} disabled={updateMut.isPending}><Icon name="check" size={14} />{updateMut.isPending ? "Kaydediliyor…" : "Kaydet"}</button>
              </div>
            </div>
          ) : null}

          {/* Onaylama paneli (üstteki "Onayla" ile açılır) */}
          {isDraft && !editing && confirming ? (
            <div style={{ marginTop: 14, border: "1px solid var(--primary)", borderRadius: "var(--radius-md)", padding: 14, background: "var(--primary-subtle)" }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 10, color: "var(--primary)" }}>Siparişi Onayla — tedarikçiye gönder</div>
              <div className="form-field">
                <label className="lbl">Onaylayan</label>
                <SearchSelect value={approverId || null} options={userOptions} onChange={(v) => setApproverId(v ?? "")} placeholder="— Onaylayan —" searchPlaceholder="Kullanıcı ara…" />
                <span className="help">Varsayılan: siz. Değiştirilebilir.</span>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn-md ghost" onClick={() => setConfirming(false)} disabled={busy || confirmMut.isPending}>Vazgeç</button>
                <button type="button" className="btn-md primary" onClick={doConfirm} disabled={busy || confirmMut.isPending}><Icon name="check" size={14} />{confirmMut.isPending ? "Onaylanıyor…" : "Onayla"}</button>
              </div>
            </div>
          ) : null}

          {/* Satırlar + toplam */}
          <div className="po-sec-title">Satırlar ({p.lines.length})</div>
          <table className="dt2" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Malzeme</th>
                <th className="num" style={{ width: 100 }}>Sipariş</th>
                <th className="num" style={{ width: 120 }}>Birim Fiyat</th>
                <th className="num" style={{ width: 120 }}>Tutar</th>
                <th className="num" style={{ width: 90 }}>Teslim</th>
                <th className="num" style={{ width: 90 }}>Kalan</th>
                <th style={{ width: 150 }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {p.lines.map((l) => {
                const remaining = l.qty - l.receivedQty;
                const canReceive = p.status !== "draft" && p.status !== "cancelled" && remaining > 0.0001;
                const isService = l.kind === "service";
                const open2 = !isService && !!l.itemId && histOpen.has(l.itemId);
                return (
                  <Fragment key={l.id}>
                    <tr>
                      <td>
                        {isService ? (
                          <><span className="badge-pill b-info" style={{ marginRight: 6 }}><Icon name="wrench" size={11} />Hizmet</span>{l.description ?? l.itemName}</>
                        ) : (
                          <>
                            <span className="mono" style={{ fontSize: 11, color: "var(--neutral-500)", marginRight: 6 }}>{l.itemCode}</span>{l.itemName}
                            <button type="button" className="po-hist-toggle" style={{ marginLeft: 8 }} onClick={() => l.itemId && toggleHist(l.itemId)}>
                              <Icon name={open2 ? "chevDown" : "chevRight"} size={11} />Geçmiş fiyat
                            </button>
                          </>
                        )}
                      </td>
                      <td className="cell-num">{fmtQty(l.qty)} {l.uom}</td>
                      <td className="cell-num">{l.unitPrice != null ? fmtMoney(l.unitPrice, cur) : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                      <td className="cell-num">{l.lineTotal != null ? fmtMoney(l.lineTotal, cur) : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                      <td className="cell-num" style={{ color: l.receivedQty > 0 ? "var(--success)" : "var(--neutral-400)" }}>{fmtQty(l.receivedQty)}</td>
                      <td className="cell-num" style={{ color: remaining > 0 ? "var(--warning)" : "var(--neutral-400)" }}>{fmtQty(remaining)}</td>
                      <td>
                        {canReceive ? (
                          isService ? (
                            <button type="button" className="btn-md ghost" style={{ height: 28, padding: "0 10px", fontSize: 12 }} onClick={() => fulfillService(l.id, remaining)} disabled={busy || receiveMut.isPending}><Icon name="check" size={13} />İfa Edildi</button>
                          ) : (
                            <button type="button" className="btn-md ghost" style={{ height: 28, padding: "0 10px", fontSize: 12 }} onClick={() => startReceive(l.id, remaining)}><Icon name="check" size={13} />Teslim Al</button>
                          )
                        ) : <span style={{ fontSize: 11, color: "var(--neutral-400)" }}>—</span>}
                      </td>
                    </tr>
                    {open2 && l.itemId ? (
                      <tr>
                        <td colSpan={7} style={{ background: "var(--neutral-50)", padding: "8px 12px" }}>
                          <PriceHistoryTable itemId={l.itemId} excludePoId={p.id} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              {p.grandTotal != null ? (
                <tr className="po-total-row">
                  <td colSpan={3} className="lbl">Genel Toplam</td>
                  <td className="cell-num">{fmtMoney(p.grandTotal, cur)}</td>
                  <td colSpan={3} />
                </tr>
              ) : null}
            </tbody>
          </table>

          {/* Teslim alma inline form */}
          {receivingLine ? (
            <div style={{ marginTop: 14, border: "1px solid var(--primary)", borderRadius: "var(--radius-md)", padding: 14, background: "var(--primary-subtle)" }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 10, color: "var(--primary)" }}>Teslim Al — lot oluşturulacak</div>
              <div className="form-row" style={{ marginBottom: 10 }}>
                <div className="form-field" style={{ margin: 0 }}>
                  <label className="lbl">Miktar</label>
                  <input className="form-input" type="number" step="any" min={0} value={recvQty} onChange={(e) => setRecvQty(e.target.value)} inputMode="decimal" />
                </div>
                <div className="form-field" style={{ margin: 0 }}>
                  <label className="lbl">Lokasyon</label>
                  <select className="form-select" value={recvLoc} onChange={(e) => setRecvLoc(e.target.value)}>
                    <option value="">— Seçin —</option>
                    {locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.code} — {loc.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row" style={{ marginBottom: 10 }}>
                <div className="form-field" style={{ margin: 0 }}>
                  <label className="lbl">Tedarikçi Parti No</label>
                  <input className="form-input" value={recvLot} onChange={(e) => setRecvLot(e.target.value)} maxLength={100} placeholder="opsiyonel" />
                </div>
                <div className="form-field" style={{ margin: 0 }}>
                  <label className="lbl">Birim Fiyat ({cur})</label>
                  <input className="form-input" type="number" step="any" min={0} value={recvPrice} onChange={(e) => setRecvPrice(e.target.value)} inputMode="decimal" placeholder="lot maliyeti" />
                </div>
              </div>
              {recvErr ? <div className="form-banner-err" role="alert" style={{ marginBottom: 10 }}>{recvErr}</div> : null}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn-md ghost" onClick={() => setReceivingLine(null)} disabled={busy}>Vazgeç</button>
                <button type="button" className="btn-md primary" onClick={() => doReceive(receivingLine)} disabled={busy || receiveMut.isPending}>{receiveMut.isPending ? "Alınıyor…" : "Teslim Al & Lot Oluştur"}</button>
              </div>
            </div>
          ) : null}

          {/* Alınan teklifler (RFQ) — firma + fiyat + doküman; onaylanan seçilince PO'ya işlenir */}
          <div className="po-sec-title" style={{ marginTop: 18 }}>Alınan Teklifler</div>
          <QuotesPanel poId={p.id} canEdit={isDraft} awardedQuoteId={p.awardedQuoteId ?? null} defaultQty={p.lines.length === 1 ? p.lines[0]!.qty : null} defaultCurrency={cur} />

          {/* Genel ekler (teklife bağlı olmayan doküman) */}
          <div className="po-sec-title" style={{ marginTop: 18 }}>Genel Ekler</div>
          <PoAttachmentPanel poId={p.id} canEdit={p.status !== "cancelled"} title="Genel Ekler" />
        </>
      )}

      <ConfirmDialog
        open={reopenOpen}
        title="Siparişi onaya geri al?"
        tone="primary"
        confirmLabel="Onaya Geri Al"
        cancelLabel="Vazgeç"
        busy={reopenMut.isPending}
        message={<>Onaylı sipariş <b>taslağa</b> döndürülecek: yeniden düzenlenip onaylanabilir. Onaylayan bilgisi silinir. (Teslim alınmış siparişler geri alınamaz.)</>}
        onConfirm={doReopen}
        onCancel={() => setReopenOpen(false)}
      />
    </Drawer>
  );
}
